import { ReactNode, ReactElement, FC, memo, useMemo } from 'react';
import {
  DateString,
  EventsLazyProvider,
} from '../../../contexts/EventsLazyContext';
import { sortTournaments } from '../../../util/tournaments/sortTournaments';
import {
  CalendarDailyProps,
  CalendarDaily,
} from '../../calendar/calendar-daily/CalendarDaily';
import {
  UseSortedEventCardsParams,
  useSortedEventCards,
} from '../../../hooks/calendar/useSortedEventCards';
import { useEventsFetcher } from '../../../hooks/calendar/useEventsFetcher';
import { DocumentData, Query } from 'firebase/firestore';
import { useVisibleDate } from '../../../hooks/calendar/useVisibleDate';
import { QueryEvents } from '../../../../functions/src/util/tournaments/EventsFetcher';
import { EventDocument } from '../../../contexts/organizer/create-event/CreateEventContext';
import { useCurrency } from '../../../contexts/CurrencyContext';

export type CalendarEventsFlexibleProps<T extends EventDocument<Date>> = Pick<
  CalendarDailyProps,
  'Title' | 'initialDate' | 'height'
> & {
  initialByDay: Record<DateString, T[]>;
  gameIds?: string[] | 'ALL';
  Wrapper?: (
    props: { children: ReactNode } & Record<string, unknown>,
  ) => ReactElement;
  Card: FC<T>;
  wrapperProps?: UseSortedEventCardsParams<
    T,
    Record<string, unknown>
  >['wrapperProps'];
  query: QueryEvents<T, Query<DocumentData>>;
};

function CalendarEventsFlexibleUnmemoized<T extends EventDocument<Date>>({
  Title,
  initialByDay,
  initialDate = new Date(),
  gameIds = 'ALL' as const,
  height = '100%',
  Wrapper,
  wrapperProps = (event) => {
    return {
      event,
    };
  },
  Card,
  query,
}: CalendarEventsFlexibleProps<T>) {
  const { formatTotalPayout } = useCurrency();
  const { sortEachDay } = useSortedEventCards({
    EventComponent: Card,
    sortFunction: (events: T[]) => {
      return sortTournaments(formatTotalPayout, events);
    },
    Wrapper,
    wrapperProps,
  });

  const { fetchEvents } = useEventsFetcher({
    sortEachDay,
    queryEvents: query,
    gameIds,
  });

  const visibleDate = useVisibleDate(initialDate);

  const eventsInitial = useMemo(() => {
    return sortEachDay(initialByDay);
  }, [initialByDay, sortEachDay]);

  return (
    <EventsLazyProvider fetchEvents={fetchEvents} eventsInitial={eventsInitial}>
      <CalendarDaily height={height} Title={Title} initialDate={visibleDate} />
    </EventsLazyProvider>
  );
}

export const CalendarEventsFlexible = memo(CalendarEventsFlexibleUnmemoized);
